import { Capacitor } from '@capacitor/core'

/**
 * Deux modèles commerciaux cohabitent, et un foyer ne change jamais de camp.
 *
 * `legacy` : les foyers inscrits avant le modèle par profil. Enfants illimités,
 * fonctionnalités avancées derrière Harmonia+. C'est le contrat qu'ils ont
 * accepté, il ne bouge pas, et les abonnements en cours continuent d'acheter
 * exactement ce qu'ils achetaient.
 *
 * `child_limit` : les foyers créés par une app qui connaît le nouveau modèle.
 * Un enfant gratuit avec TOUT le produit, les profils suivants derrière
 * Harmonia+.
 *
 * La cohorte est décidée à la CRÉATION du foyer et jamais réévaluée. C'est
 * l'app qui la déclare, pas une date de bascule : les versions déjà installées
 * n'envoient rien et créent donc des foyers `legacy`, qu'elles savent faire
 * fonctionner. Sans ça, il existerait une fenêtre entre le déploiement de la
 * base et l'approbation store où une app ignorante se prendrait un refus
 * serveur au lieu d'un paywall.
 */
export type BusinessCohort = 'legacy' | 'child_limit'

/**
 * La cohorte que cette version de l'app pose sur les foyers qu'elle crée,
 * quand elle est en mesure de la tenir. Voir `cohortForNewHousehold`.
 */
export const CURRENT_BUSINESS_COHORT: BusinessCohort = 'child_limit'

/** Le strict nécessaire pour situer un foyer, pour que les tests n'aient pas à en bâtir un complet. */
export interface CohortHousehold {
  business_cohort?: string | null
}

/**
 * Ce foyer vit-il sous l'ancien contrat ?
 *
 * Tout ce qui n'est pas EXPLICITEMENT `child_limit` compte comme ancien :
 * valeur absente, nulle, ou inconnue. C'est délibéré et c'est le sens sûr.
 * Une réponse serveur tronquée, un cache d'une version antérieure ou une
 * valeur qu'on n'a pas prévue donnent alors un foyer trop généreux, jamais un
 * parent qui trouve ses enfants verrouillés sans comprendre.
 */
export function isLegacyCohort(household: CohortHousehold | null | undefined): boolean {
  return household?.business_cohort !== 'child_limit'
}

/**
 * Ce client peut-il encaisser ?
 *
 * Le natif, oui, par l'App Store ou Play. Le web seulement si une clé Web
 * Billing est posée : sans elle le paywall web est désactivé au boot
 * (`initRevenueCat`), et une clé de boutique de test (`test_…`) n'encaisse
 * rien non plus.
 */
export function hasCheckout(isNative: boolean, webBillingKey: string | null | undefined): boolean {
  if (isNative) return true
  const key = (webBillingKey ?? '').trim()
  return key.length > 0 && !key.startsWith('test_')
}

/**
 * La cohorte que ce client a le droit de poser sur un foyer qu'il crée.
 *
 * Le modèle par profil met les enfants au-delà du premier derrière Harmonia+.
 * Le poser depuis un client qui n'a pas de caisse, c'est construire un mur
 * sans porte : le parent bute au deuxième enfant et n'a aucun moyen de payer.
 * Un client sans caisse crée donc des foyers `legacy`, qui n'ont pas de limite
 * d'enfants et n'ont donc rien à acheter pour continuer.
 *
 * C'est la PWA que ça vise. Elle se déploie depuis `main` sans passer par une
 * revue store, elle est donc en avance sur les apps natives par construction,
 * et elle n'a aujourd'hui aucune clé RevenueCat. Le 2026-09-07 elle avait déjà
 * créé deux foyers `child_limit` en production alors que les stores servaient
 * encore des 2.x. Le jour où la clé Web Billing est posée dans Vercel, le web
 * bascule tout seul : il n'y a pas de drapeau à penser à retirer.
 */
export function cohortForNewHousehold(
  isNative: boolean,
  webBillingKey: string | null | undefined,
): BusinessCohort {
  return hasCheckout(isNative, webBillingKey) ? CURRENT_BUSINESS_COHORT : 'legacy'
}

/**
 * Les champs à poser sur un foyer que CETTE app crée.
 *
 * À étaler sur tout insert de `households` et dans le payload de
 * `commit_onboarding_draft`. Un chemin de création qui les oublie rend un
 * foyer `legacy` : ça coûte un abonnement potentiel, ça ne bloque personne.
 */
export function newHouseholdCohortFields(): {
  business_cohort: BusinessCohort
  created_on_app_version: string | null
} {
  return {
    business_cohort: cohortForNewHousehold(
      Capacitor.isNativePlatform(),
      import.meta.env.VITE_REVENUECAT_WEB_API_KEY,
    ),
    // Lu depuis la version du bundle plutôt qu'écrit en dur : le passage en
    // 2.7.0 suit tout seul et les deux ne peuvent pas se désynchroniser.
    created_on_app_version: import.meta.env.VITE_APP_VERSION ?? null,
  }
}

/**
 * L'offering RevenueCat servi aux foyers du modèle par profil.
 *
 * C'est le NOUVEAU contrat qui se nomme, pas l'ancien, et l'ordre n'est pas
 * un détail de style. Le Current du dashboard est ce que reçoit toute app qui
 * ne demande rien, et il y en aura toujours : les versions déjà installées
 * ignorent les cohortes et appellent `presentPaywall()` sans options. Le
 * Current doit donc rester l'ancien paywall aussi longtemps qu'une 2.x tourne
 * sur un téléphone. Nommer l'ancien et laisser le Current au nouveau, c'était
 * promettre le nouveau modèle à toute la base installée le jour où on bascule
 * le dashboard.
 *
 * Écrit en dur, relevé dans le dashboard RevenueCat. Le SDK cherche cette
 * chaîne dans `offerings.all`, qui est indexé sur l'IDENTIFIER de l'offering
 * (sa lookup key) et non sur l'API ID `ofrng…` de l'API v2 : c'est
 * l'identifier qu'il faut poser ici. Relevé le 2026-09-07 sur
 * `GET /v1/subscribers/<id>/offerings`, qui renvoie les trois identifiers
 * `default`, `default-trial` et `default-onechild`, et confirme que
 * `default-onechild` est le seul à porter `$rc_lifetime`. Introuvable, l'app retombe sur le
 * Current, donc sur l'ancien paywall : un foyer du nouveau modèle se verrait
 * vendre une promesse qui n'est pas la sienne, sans rien casser ni rien
 * lever. La console liste alors les identifiants réellement renvoyés.
 *
 * Les deux offerings débloquent le même entitlement : c'est lui, pas le
 * produit acheté, que lit tout le gating. Un achat à vie fait par ce chemin
 * ouvre donc exactement ce qu'ouvre un abonnement.
 */
export const CHILD_LIMIT_OFFERING_ID = 'default-onechild'

/**
 * Quel offering présenter à ce foyer ?
 *
 * `null` = le Current du dashboard, c'est-à-dire l'ancien paywall, qui ne
 * bouge plus. Il reste la valeur par défaut de l'ancien contrat ET de toute
 * app trop ancienne pour poser la question.
 *
 * Un foyer inconnu (cohorte absente, nulle, cache d'une version antérieure)
 * reçoit donc l'ancien paywall, comme `isLegacyCohort` le veut partout
 * ailleurs : c'est l'offre qui ne promet rien qu'on ne puisse tenir.
 */
export function paywallOfferingId(household: CohortHousehold | null | undefined): string | null {
  return isLegacyCohort(household) ? null : CHILD_LIMIT_OFFERING_ID
}
